/**
 * Runtime config schema. Loaded from `/config.json` by `configService`.
 *
 * Mirrors the gen5-pwa-fresh pattern: per-environment JSON files in
 * public/ that get swapped at deploy time without rebuilding.
 */

export type Environment = "development" | "testing" | "stage" | "production";

export interface FeatureFlags {
  enableSentry: boolean;
  enableDebugLogging: boolean;
}

export interface MttrConfig {
  /** URL the Uptime page polls. Optional — falls back to `/uptime.json`. */
  uptimeJsonPath?: string;
  /** URL the Recovery page polls. Optional — falls back to `/recovery.json`. */
  recoveryJsonPath?: string;
  /** How often to refetch /api/boxes. */
  pollIntervalSeconds: number;
  /** Base URL for the portal application API (mttr/app_api: login, /me,
   *  /api/boxes, and the scoped customer data endpoints).
   *  Dev: 'http://localhost:8091'. Prod: same-origin '' (nginx proxies
   *  /api/*). Login is unavailable when this resolves to null. */
  appApiBaseUrl?: string;
}

/** Controls the Dashboard's "under construction" notice. */
export interface UnderConstructionConfig {
  /** Omit or set true to show the banner; only an explicit false hides it. */
  enabled: boolean;
  /** Optional override for the banner copy. */
  message?: string;
}

export interface AppConfig {
  environment: Environment;
  mttr: MttrConfig;
  features: FeatureFlags;
  /** Optional — when absent the banner shows (see UnderConstructionBanner). */
  underConstruction?: UnderConstructionConfig;
}

/** Defaults used when config.json fails to load. */
export const DEFAULT_CONFIG: AppConfig = {
  environment: "development",
  mttr: {
    uptimeJsonPath: "/uptime.json",
    recoveryJsonPath: "/recovery.json",
    pollIntervalSeconds: 30,
    appApiBaseUrl: "http://localhost:8091",
  },
  features: {
    enableSentry: false,
    enableDebugLogging: true,
  },
  underConstruction: { enabled: true },
};
